import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "effector-react";
import { useUnit } from "effector-react";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { $sectorsTerms } from "@/core/store/terms";
import { Idea } from "../model/types";
import { ideasService } from "../service/api";
import { $authStore } from "@/features/auth/store/auth";
import { useToast } from "@/core/hooks/useToast";
import "./IdeaListItem.scss";
import { imageList } from "@/core/utils/imageList";
import { Tags } from "@/core/components/tags/Tags";
import { BgImage } from "@/core/components/bgImage/BgImage";

interface IdeaListItemProps {
  idea: Idea;
  isMyIdea: boolean;
  onBookmark?: (ideaId: string, isBookmarked: boolean) => void;
  type?: "invitation" | "request";
  onActionComplete?: () => void;
  action_id?: string;
}

export const IdeaListItem: React.FC<IdeaListItemProps> = ({
  idea,
  onBookmark,
  isMyIdea,
  type,
  onActionComplete,
  action_id,
}) => {
  const [loading, setLoading] = useState<"accept" | "deny" | "request" | null>(
    null,
  );
  const { user } = useUnit($authStore);
  const { showError } = useToast();

  // Get sectors terms from store
  const sectorsTerms = useUnit($sectorsTerms);

  // Helper function to check if a value is a numeric string (ID)
  const isNumericString = (value: string): boolean => {
    return /^\d+$/.test(value);
  };

  // Helper function to get sector name from ID using terms store
  const getSectorNameFromId = (
    sectorValue: string | { name: string; id: string },
  ): string => {
    // If it's an object with name property, return the name
    if (typeof sectorValue === "object" && sectorValue?.name) {
      return sectorValue.name;
    }

    // If it's a string
    if (typeof sectorValue === "string") {
      // If it's not a numeric string, return as-is (it's already a name)
      if (!isNumericString(sectorValue)) {
        return sectorValue;
      }

      // It's a numeric string, so look up the name in terms store
      if (sectorsTerms.data && sectorsTerms.data.length > 0) {
        const sector = sectorsTerms.data.find(
          (term) => term.tid === sectorValue,
        );
        return sector ? sector.name : sectorValue;
      }
    }

    return String(sectorValue); // Fallback
  };

  const handleWithdrawRequest = async (ideaId: string) => {
    try {
      setLoading("accept");
      await ideasService.withdrawRequest(ideaId);
      onActionComplete?.();
    } catch (error: any) {
      showError(error?.error || "Only idea owner can answer to requests");
    } finally {
      setLoading(null);
    }
  };
  const handleAcceptInvitation = async (action_id: string) => {
    try {
      setLoading("accept");
      await ideasService.acceptDenyInvitation("accept", action_id);
      onActionComplete?.();
    } catch (error: any) {
      showError(error || "Failed to accept invitation");
    } finally {
      setLoading(null);
    }
  };

  const handleDenyInvitation = async (action_id: string) => {
    try {
      setLoading("deny");
      await ideasService.acceptDenyInvitation("deny", action_id);
      onActionComplete?.();
    } catch (error: any) {
      showError(error || "Failed to deny invitation");
    } finally {
      setLoading(null);
    }
  };

  const handleRequestToJoin = async () => {
    if (!user?.uid) {
      console.error("User not authenticated or uid missing", {
        userId: user?.id,
        userUid: user?.uid,
        user: user,
      });
      return;
    }

    try {
      setLoading("request");
      await ideasService.requestJoinIdea(
        idea.id,
        user.uid.toString(),
        "request",
      );
      onActionComplete?.();
    } catch (error: any) {
      showError(error || "Failed to request to join idea");
    } finally {
      setLoading(null);
    }
  };

  return (
    <Link to={`/ideas/${idea.id}`} className="idea-list-item">
      {/* First row with two columns */}
      <div className="idea-badges">
        {idea?.is_owner && <div className="idea-badge">Owner</div>}
        {idea?.is_member && !idea?.is_owner && (
          <div className="idea-badge">Guest</div>
        )}
        {idea?.is_coordinator && !idea?.is_owner && (
          <div className="idea-badge">Coordinator</div>
        )}
        {idea.state.toLowerCase() !== "approved" && (
          <div className="idea-badge"> {idea.state}</div>
        )}
      </div>
      <div className="first-row">
        {/* First column: title, description, tags */}
        <div className="content-column">
          <h2 className="idea-title">
            <div>
              {typeof idea.title === "string" ? idea.title : String(idea.title)}
            </div>
          </h2>

          <p className="idea-description">
            {idea.description
              ? typeof idea.description === "string"
                ? idea.description.length > 300
                  ? `${idea.description.substring(0, 300)}...`
                  : idea.description
                : String(idea.description)
              : ""}
            {/* <pre>{JSON.stringify(idea, null, 2)}</pre> */}
          </p>

          <div className="idea-tags">
            {idea.sectors && idea.sectors.length > 0 && (
              <>
                {idea.sectors.slice(0, 4).map((sector, index) => {
                  const sectorName = getSectorNameFromId(sector);
                  return (
                    <span
                      key={`sector-tag-${idea.id}-${index}`}
                      className="sector-tag"
                    >
                      {sectorName}
                    </span>
                  );
                })}
                {idea.sectors.length > 4 && (
                  <span className="sector-tag more-count">
                    +{idea.sectors.length - 4}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Second column: Programme, Leadership, Looking for */}
        <div className="info-column">
          <div className="info-row programme-row">
            <div className="info-label">Programme</div>
            <div className="programme-badge">
              <div className="eu-flag">
                <img
                  src={
                    idea.programmes && idea.programmes.length > 0
                      ? idea.programmes[0].logo
                      : imageList.noimage
                  }
                  alt="EU call"
                />
              </div>
              <span>
                {idea.programmes &&
                  idea.programmes.length > 0 &&
                  idea.programmes[0].name}
              </span>
            </div>
          </div>
          {idea.is_member ? (
            <div className="interested-partners mb-0">
              <div className="info-label">Partnership</div>
              {idea.partnership &&
                (Array.isArray(idea.partnership)
                  ? idea.partnership.length > 0
                  : Object.keys(idea.partnership).length > 0) && (
                  <div className="partnership-logos">
                    <div className="partners-avatars">
                      {Object.values(idea?.partnership)
                        ?.slice(0, 4)
                        .map((p, i) => (
                          <div
                            key={p.id || i}
                            className="partner-avatar-wrapper"
                            style={{
                              transform:
                                i > 0 ? `translateX(-${25 * i}px)` : "none",
                              zIndex: i > 0 ? 2 + (i - 1) : 1,
                              marginLeft: "10px",
                            }}
                          >
                            <div className="company-logo-small placeholder">
                              <BgImage
                                img={p.logo_uri}
                                altImage={imageList.noimage}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              {idea.partnership && Array.isArray(idea.partnership)
                ? idea.partnership.length > 0
                : Object.keys(idea.partnership).length > 0 &&
                  Object.keys(idea.partnership).length > 4 && (
                    <span
                      className="more-count-small"
                      style={{
                        transform: `translateX(-${21 * 4}px)`,
                        zIndex: 2 + Object.values(idea?.partnership).length,
                      }}
                    >
                      +{Object.values(idea?.partnership).length - 4}
                    </span>
                  )}
            </div>
          ) : (
            <div className="info-row leadership-row">
              <div className="info-label">Partnership</div>
              <div className="leadership-value">
                <span className="count">
                  <Tags
                    type="partner"
                    number={
                      idea.partnership
                        ? Array.isArray(idea.partnership)
                          ? idea.partnership_count
                          : typeof idea.partnership === "object" &&
                              idea.partnership !== null
                            ? Object.keys(idea.partnership).length
                            : 0
                        : 0
                    }
                  />
                </span>
              </div>
            </div>
          )}
          <div className="info-row looking-for-row">
            <div className="info-label">Looking for</div>
            <div className="looking-for-value">
              {idea.looking_for && idea.looking_for.length > 0
                ? idea.looking_for
                    .map((item) => {
                      if (typeof item === "string") {
                        return item;
                      } else if (
                        item &&
                        typeof item === "object" &&
                        "name" in item
                      ) {
                        return item.name;
                      }
                      return "";
                    })
                    .filter(Boolean)
                    .join(", ")
                : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Second row: action buttons */}
      {isMyIdea && (
        <div className="second-row">
          <div className="action-buttons">
            {type === "invitation" && (
              <button
                className="action-btn accept-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  action_id && handleAcceptInvitation(action_id);
                }}
                disabled={loading !== null}
              >
                <SvgIcon icon="icon-accept" />
                {loading === "accept"
                  ? "Accepting..."
                  : "Accept invitation and join idea"}
              </button>
            )}
            {type === "invitation" && (
              <button
                className="action-btn deny-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  action_id && handleDenyInvitation(action_id);
                }}
                disabled={loading !== null}
              >
                <SvgIcon icon="icon-deny" />
                {loading === "deny" ? "Denying..." : "Deny invitation"}
              </button>
            )}
            {type === "request" && (
              <button
                className="action-btn deny-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  idea.id && handleWithdrawRequest(idea.id);
                }}
                disabled={loading !== null}
              >
                <SvgIcon icon="icon-deny" />
                {loading === "deny" ? "Withdrawing..." : "Withdraw request"}
              </button>
            )}
            {/* <button className="action-btn request-btn" onClick={handleRequestToJoin} disabled={loading !== null}>
              <SvgIcon icon="icon-plus" />
              <div>{loading === 'request' ? 'Requesting...' : 'REQUEST TO JOIN'}</div>
            </button> */}
          </div>
        </div>
      )}
    </Link>
  );
};
